import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  ListTodo, Trash2, Send, FileText, CheckCircle2, XCircle,
  Sparkles, Globe, ChevronLeft, ChevronRight, Eye, Edit2,
  Filter, SquareCheck, Square, Loader2, ArrowRight, Image,
  Copy, Tag, Search, Plus, DollarSign, RefreshCw, X, ExternalLink,
  FileStack, Link2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { SafeHtml } from "@/components/SafeHtml";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: "Szkic", bg: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
  pending: { label: "Oczekujacy", bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  published: { label: "Opublikowany", bg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  rejected: { label: "Odrzucony", bg: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" },
};

export default function PostQueue() {
  const { t } = useLanguage();
  const [queueData, setQueueData] = useState({ posts: [], total: 0, page: 1, total_pages: 0 });
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [publishing, setPublishing] = useState(null);
  
  // Dialog podgladu
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [previewTab, setPreviewTab] = useState("content");
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editMetaTitle, setEditMetaTitle] = useState("");
  const [editMetaDesc, setEditMetaDesc] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editImageAlt, setEditImageAlt] = useState("");
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  
  // Dialog produktu afiliacyjnego
  const [affiliateDialog, setAffiliateDialog] = useState(false);
  const [affiliatePosition, setAffiliatePosition] = useState("end"); // "start" or "end"
  const [affiliateProducts, setAffiliateProducts] = useState([]);
  const [affiliateSearch, setAffiliateSearch] = useState("");
  const [affiliateCategory, setAffiliateCategory] = useState("all");
  
  // Media
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [mediaCategories, setMediaCategories] = useState([]);
  const [mediaFilterCategory, setMediaFilterCategory] = useState("all");
  const [mediaSearch, setMediaSearch] = useState("");
  
  // Artykuły wspierające
  const [supportDialog, setSupportDialog] = useState(false);
  const [supportPost, setSupportPost] = useState(null);
  const [supportCount, setSupportCount] = useState(3);
  const [generatingSupport, setGeneratingSupport] = useState(false);
  const [generatedSupport, setGeneratedSupport] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), per_page: "10" });
      if (filterStatus !== "all") params.set("status", filterStatus);

      const [queueRes, statsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/posts/queue?${params.toString()}`),
        axios.get(`${API}/posts/queue/stats`),
        axios.get(`${API}/sites`),
      ]);
      setQueueData(queueRes.data);
      setStats(statsRes.data);
      setSites(sitesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus, page]);

  useEffect(() => { fetchData(); setSelected([]); }, [fetchData]);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/media`);
      const media = res.data || [];
      setMediaLibrary(media);
      
      // Wyciagnij unikalne kategorie
      const cats = [...new Set(media.map(m => m.category).filter(Boolean))];
      setMediaCategories(cats.sort());
    } catch (err) { console.error(err); }
  };

  const fetchAffiliateProducts = async () => {
    try {
      const res = await axios.get(`${API}/affiliates`);
      setAffiliateProducts(res.data || []);
    } catch (err) { console.error(err); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === queueData.posts.length) {
      setSelected([]);
    } else {
      setSelected(queueData.posts.map(p => p.id));
    }
  };

  const handleBulkApprove = async () => {
    if (!selected.length) return;
    try {
      const res = await axios.post(`${API}/posts/queue/bulk-approve`, { post_ids: selected });
      toast.success(`Zatwierdzono: ${res.data.approved}`);
      setSelected([]);
      fetchData();
    } catch (err) { toast.error("Blad"); }
  };

  const handleBulkReject = async () => {
    if (!selected.length) return;
    try {
      const res = await axios.post(`${API}/posts/queue/bulk-reject`, { post_ids: selected });
      toast.success(`Odrzucono: ${res.data.rejected}`);
      setSelected([]);
      fetchData();
    } catch (err) { toast.error("Blad"); }
  };

  const handlePublish = async (postId) => {
    setPublishing(postId);
    try {
      await axios.post(`${API}/posts/${postId}/publish`);
      toast.success("Opublikowano");
      fetchData();
    } catch (err) { toast.error("Blad publikacji"); }
    finally { setPublishing(null); }
  };

  const handleAssignSite = async (postId, siteId) => {
    try {
      await axios.post(`${API}/posts/${postId}/assign-site`, { site_id: siteId });
      toast.success("Przypisano");
      fetchData();
    } catch (err) { toast.error("Blad"); }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`);
      if (selectedPost?.id === postId) setSelectedPost(null);
      if (previewPost?.id === postId) { setPreviewDialog(false); setPreviewPost(null); }
      toast.success("Usunieto");
      fetchData();
    } catch (err) { toast.error("Blad"); }
  };

  const handleStatusChange = async (postId, newStatus) => {
    try {
      const post = queueData.posts.find(p => p.id === postId);
      if (post) {
        await axios.put(`${API}/posts/${postId}`, { ...post, status: newStatus });
        toast.success("Zmieniono status");
        fetchData();
      }
    } catch (err) { toast.error("Blad"); }
  };

  // Otwórz podgląd artykułu
  const openPreview = (post) => {
    setPreviewPost(post);
    setEditContent(post.content || "");
    setEditTitle(post.title || "");
    // Pobierz meta dane z seo_meta jeśli brak bezpośrednich pól
    const seoMeta = post.seo_meta || {};
    setEditMetaTitle(post.meta_title || seoMeta.meta_title || post.title || "");
    setEditMetaDesc(post.meta_description || seoMeta.meta_description || "");
    const tags = post.tags || seoMeta.tags || [];
    setEditTags(Array.isArray(tags) ? tags.join(", ") : tags);
    // Alt zdjęcia
    const featuredImg = post.featured_image || {};
    setEditImageAlt(post.featured_image_alt || post.image_alt || featuredImg.alt || "");
    setSelectedImage(featuredImg.url || post.featured_image || null);
    setPreviewTab("content");
    setEditMode(false);
    setPreviewDialog(true);
    setMediaFilterCategory("all");
    setMediaSearch("");
    fetchMedia();
    fetchAffiliateProducts();
  };

  // Zapisz zmiany
  const handleSavePost = async () => {
    if (!previewPost) return;
    setSaving(true);
    try {
      await axios.put(`${API}/posts/${previewPost.id}`, {
        ...previewPost,
        title: editTitle,
        content: editContent,
        meta_title: editMetaTitle,
        meta_description: editMetaDesc,
        tags: editTags.split(",").map(t => t.trim()).filter(t => t),
        featured_image_alt: editImageAlt,
        image_alt: editImageAlt,
        featured_image: selectedImage,
      });
      toast.success("Zapisano zmiany");
      setEditMode(false);
      fetchData();
      // Odśwież podgląd
      const res = await axios.get(`${API}/posts/${previewPost.id}`);
      if (res.data) setPreviewPost(res.data);
    } catch (err) { toast.error("Blad zapisu"); }
    finally { setSaving(false); }
  };

  // Popraw artykuł AI
  const handleImproveArticle = async () => {
    if (!previewPost) return;
    setImproving(true);
    try {
      const res = await axios.post(`${API}/ai/improve-article`, {
        content: editContent,
        title: editTitle,
      });
      if (res.data?.improved_content) {
        setEditContent(res.data.improved_content);
        toast.success("Artykul poprawiony przez AI");
      }
    } catch (err) { toast.error("Blad poprawiania"); }
    finally { setImproving(false); }
  };

  // Dodaj produkt afiliacyjny z listy
  const handleAddAffiliateProduct = (product) => {
    if (!product) return;
    
    const productHtml = `
<div style="background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); border: 2px solid #4caf50; border-radius: 12px; padding: 20px; margin: 20px 0;">
  ${product.image_url ? `<img src="${product.image_url}" alt="${product.name}" style="max-width: 200px; margin-bottom: 15px; border-radius: 8px;" />` : ""}
  <h3 style="color: #2e7d32; margin: 0 0 10px 0; font-size: 1.2em;">💎 ${product.name}</h3>
  <p style="color: #424242; margin: 0 0 15px 0; line-height: 1.6;">${product.description || ""}</p>
  <a href="${product.url}" target="_blank" rel="nofollow sponsored" style="display: inline-block; background: #4caf50; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: bold;">${product.cta_text || "Sprawdź ofertę"} →</a>
</div>`;

    if (affiliatePosition === "start") {
      setEditContent(productHtml + "\n\n" + editContent);
    } else {
      setEditContent(editContent + "\n\n" + productHtml);
    }
    
    setAffiliateDialog(false);
    toast.success(`Dodano "${product.name}" na ${affiliatePosition === "start" ? "poczatku" : "koncu"} artykulu`);
    setEditMode(true);
    
    // Zwieksz licznik uzyc
    axios.post(`${API}/affiliates/${product.id}/track`, { type: "insertion" }).catch(() => {});
  };

  // Kopiuj do schowka
  const handleCopy = () => {
    navigator.clipboard.writeText(editContent);
    toast.success("Skopiowano do schowka");
  };

  // Otwórz dialog artykułów wspierających
  const openSupportDialog = (post) => {
    setSupportPost(post);
    setSupportCount(3);
    setGeneratedSupport([]);
    setSupportDialog(true);
  };

  // Generuj artykuły wspierające
  const handleGenerateSupport = async () => {
    if (!supportPost) return;
    setGeneratingSupport(true);
    try {
      // Token jest ustawiony globalnie przez AuthContext
      const res = await axios.post(`${API}/posts/${supportPost.id}/generate-support`, 
        { count: supportCount }
      );
      setGeneratedSupport(res.data.articles || []);
      toast.success(`Wygenerowano ${res.data.generated} artykulow wspierajacych`);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Blad generowania artykulow wspierajacych"); 
    }
    finally { setGeneratingSupport(false); }
  };

  const posts = queueData.posts;

  return (
    <div data-testid="queue-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Kolejka Postow</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">Zarzadzaj artykułami do publikacji</p>
        </div>
      </div>

      {/* Statystyki - klikalne filtry */}
      {stats && (
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "Wszystkie", value: stats.total, color: "text-slate-500", bgActive: "bg-slate-100 ring-2 ring-slate-300", filter: "all" },
            { label: "Szkice", value: stats.draft, color: "text-slate-600", bgActive: "bg-slate-200 ring-2 ring-slate-400", filter: "draft" },
            { label: "Oczekujace", value: stats.pending, color: "text-amber-500", bgActive: "bg-amber-100 ring-2 ring-amber-300", filter: "pending" },
            { label: "Opublikowane", value: stats.published, color: "text-emerald-500", bgActive: "bg-emerald-100 ring-2 ring-emerald-300", filter: "published" },
            { label: "Odrzucone", value: stats.rejected, color: "text-red-500", bgActive: "bg-red-100 ring-2 ring-red-300", filter: "rejected" },
          ].map((s, i) => (
            <Card 
              key={i} 
              data-testid={`queue-stat-${s.filter}`} 
              className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md ${filterStatus === s.filter ? s.bgActive : "bg-card hover:bg-muted/50"}`}
              onClick={() => { setFilterStatus(s.filter); setPage(1); }}
            >
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filtry + masowe akcje */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
            <SelectTrigger data-testid="queue-status-filter" className="h-8 text-xs w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Wszystkie</SelectItem>
              <SelectItem value="draft" className="text-xs">Szkice</SelectItem>
              <SelectItem value="pending" className="text-xs">Oczekujace</SelectItem>
              <SelectItem value="published" className="text-xs">Opublikowane</SelectItem>
              <SelectItem value="rejected" className="text-xs">Odrzucone</SelectItem>
            </SelectContent>
          </Select>
          {queueData.total > 0 && (
            <span className="text-[10px] text-muted-foreground">
              {queueData.total} wszystkich
            </span>
          )}
        </div>

        {selected.length > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs rounded-md">{selected.length} wybranych</Badge>
            <Button variant="outline" size="sm" data-testid="bulk-approve-btn"
              onClick={handleBulkApprove} className="h-7 text-xs gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" /> Zatwierdz
            </Button>
            <Button variant="outline" size="sm" data-testid="bulk-reject-btn"
              onClick={handleBulkReject} className="h-7 text-xs gap-1 text-red-500">
              <XCircle className="w-3 h-3" /> Odrzuc
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tabela */}
        <div className={selectedPost ? "lg:col-span-2" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {posts.length === 0 ? (
                <div data-testid="no-posts" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <ListTodo className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">Brak postow w kolejce</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox data-testid="select-all-cb"
                            checked={selected.length === posts.length && posts.length > 0}
                            onCheckedChange={toggleSelectAll} />
                        </TableHead>
                        <TableHead className="text-xs font-semibold">Tytul</TableHead>
                        <TableHead className="text-xs font-semibold">Status</TableHead>
                        <TableHead className="text-xs font-semibold">Link</TableHead>
                        <TableHead className="text-xs font-semibold">Strona</TableHead>
                        <TableHead className="text-xs font-semibold">Slow</TableHead>
                        <TableHead className="text-xs font-semibold">Data</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Akcje</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posts.map((post) => {
                        const stCfg = statusConfig[post.status] || statusConfig.draft;
                        return (
                          <TableRow key={post.id} data-testid={`queue-row-${post.id}`}
                            className={`cursor-pointer ${selectedPost?.id === post.id ? "bg-primary/5" : ""}`}
                            onClick={() => setSelectedPost(post)}>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selected.includes(post.id)}
                                onCheckedChange={() => toggleSelect(post.id)} />
                            </TableCell>
                            <TableCell className="text-xs font-medium max-w-[200px] truncate">{post.title}</TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] border-0 rounded-md ${stCfg.bg}`}>{stCfg.label}</Badge>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {post.published_url ? (
                                <a 
                                  href={post.published_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
                                  title={post.published_url}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Zobacz
                                </a>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {sites.find(s => s.id === post.site_id)?.name || "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{post.word_count || 0}</TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {new Date(post.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600"
                                  onClick={() => openPreview(post)} title="Podglad">
                                  <Eye className="w-3 h-3" strokeWidth={1.5} />
                                </Button>
                                {post.status === "published" && post.published_url && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-purple-600"
                                    data-testid={`support-${post.id}`}
                                    onClick={() => openSupportDialog(post)}
                                    title="Generuj artykuly wspierajace">
                                    <FileStack className="w-3 h-3" strokeWidth={1.5} />
                                  </Button>
                                )}
                                {post.status !== "published" && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600"
                                    data-testid={`publish-${post.id}`}
                                    onClick={() => handlePublish(post.id)}
                                    disabled={publishing === post.id}>
                                    {publishing === post.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                                     <Send className="w-3 h-3" strokeWidth={1.5} />}
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                  onClick={() => handleDelete(post.id)}>
                                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Paginacja */}
                  {queueData.total_pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                      <span className="text-[10px] text-muted-foreground">
                        Strona {queueData.page} / {queueData.total_pages}
                      </span>
                      <div className="flex gap-1">
                        <Button variant="outline" size="icon" className="h-7 w-7"
                          disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-7 w-7"
                          disabled={page >= queueData.total_pages} onClick={() => setPage(p => p + 1)}>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail panel */}
        {selectedPost && (
          <div className="lg:col-span-1">
            <Card data-testid="queue-detail" className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-heading text-foreground leading-snug pr-2">{selectedPost.title}</CardTitle>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-destructive"
                    onClick={() => handleDelete(selectedPost.id)}>
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-2 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  <Badge className={`text-[10px] border-0 rounded-md ${(statusConfig[selectedPost.status] || statusConfig.draft).bg}`}>
                    {(statusConfig[selectedPost.status] || statusConfig.draft).label}
                  </Badge>
                  {selectedPost.ai_model && (
                    <Badge variant="outline" className="text-[10px] rounded-md">{selectedPost.ai_model.toUpperCase()}</Badge>
                  )}
                  {selectedPost.word_count > 0 && (
                    <Badge variant="secondary" className="text-[10px] rounded-md">{selectedPost.word_count} slow</Badge>
                  )}
                  {selectedPost.is_supporting && (
                    <Badge variant="outline" className="text-[10px] rounded-md bg-purple-100 text-purple-700">Wspierajacy</Badge>
                  )}
                </div>

                {/* Link do opublikowanego artykulu */}
                {selectedPost.published_url && (
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-[10px] uppercase font-semibold text-emerald-700 dark:text-emerald-400 tracking-wider mb-1.5 flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Link do artykulu
                    </p>
                    <a 
                      href={selectedPost.published_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      {selectedPost.published_url}
                    </a>
                  </div>
                )}

                {/* Podglad tresci */}
                {selectedPost.content && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">Podglad</p>
                    <p className="text-xs text-foreground/80 leading-relaxed line-clamp-6">
                      {selectedPost.content.replace(/<[^>]*>/g, '').substring(0, 400)}
                      {selectedPost.content.length > 400 && "..."}
                    </p>
                  </div>
                )}

                {/* Przycisk podgladu pelnego */}
                <Button variant="outline" size="sm" className="w-full h-8 text-xs gap-1.5"
                  onClick={() => openPreview(selectedPost)}>
                  <Eye className="w-3 h-3" /> Pelny podglad i edycja
                </Button>

                {/* Assign site */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Przypisz strone</p>
                  <Select value={selectedPost.site_id || ""}
                    onValueChange={(v) => handleAssignSite(selectedPost.id, v)}>
                    <SelectTrigger data-testid="assign-site-select" className="h-8 text-xs">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Akcje */}
                <div className="flex flex-col gap-1.5">
                  {selectedPost.status !== "published" && (
                    <Button size="sm" data-testid="publish-post-btn"
                      onClick={() => handlePublish(selectedPost.id)}
                      disabled={publishing === selectedPost.id}
                      className="h-8 text-xs gap-1.5 rounded-md w-full">
                      {publishing === selectedPost.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                       <Send className="w-3 h-3" strokeWidth={1.5} />}
                      Publikuj teraz
                    </Button>
                  )}
                  {selectedPost.status === "published" && selectedPost.published_url && (
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 rounded-md w-full text-purple-600 border-purple-300"
                      onClick={() => openSupportDialog(selectedPost)}>
                      <FileStack className="w-3 h-3" /> Generuj artykuly wspierajace
                    </Button>
                  )}
                  {selectedPost.status === "draft" && (
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 rounded-md w-full text-amber-600"
                      onClick={() => handleStatusChange(selectedPost.id, "pending")}>
                      <CheckCircle2 className="w-3 h-3" /> Do oczekujacych
                    </Button>
                  )}
                  {selectedPost.status !== "rejected" && selectedPost.status !== "published" && (
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 rounded-md w-full text-red-500"
                      onClick={() => handleStatusChange(selectedPost.id, "rejected")}>
                      <XCircle className="w-3 h-3" /> Odrzuc
                    </Button>
                  )}
                </div>

                {/* Info o artykulach wspierajacych */}
                {selectedPost.support_articles && selectedPost.support_articles.length > 0 && (
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <p className="text-[10px] uppercase font-semibold text-purple-700 dark:text-purple-400 tracking-wider mb-1.5 flex items-center gap-1">
                      <FileStack className="w-3 h-3" /> Artykuly wspierajace ({selectedPost.support_articles.length})
                    </p>
                    <div className="space-y-1">
                      {selectedPost.support_articles.map((sa, idx) => (
                        <div key={idx} className="text-[10px] text-muted-foreground">
                          • {sa.title?.substring(0, 50)}... ({sa.site})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-[9px] text-muted-foreground/60">
                  Utworzono: {new Date(selectedPost.created_at).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* ============ DIALOG PODGLADU ARTYKULU ============ */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="sm:max-w-none max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg font-heading">Podglad wygenerowanej tresci</DialogTitle>
          </DialogHeader>
          
          {previewPost && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Górny pasek z przyciskami */}
              <div className="flex flex-wrap items-center gap-2 py-3 border-b">
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 text-orange-600 border-orange-300"
                  onClick={handleImproveArticle} disabled={improving}>
                  {improving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Popraw artykul
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 text-green-600 border-green-300"
                  onClick={() => setAffiliateDialog(true)}>
                  <DollarSign className="w-3 h-3" /> Dodaj produkt afiliacyjny
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
                  onClick={() => setEditMode(!editMode)}>
                  <Edit2 className="w-3 h-3" /> {editMode ? "Podglad" : "Edytuj"}
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
                  onClick={handleCopy}>
                  <Copy className="w-3 h-3" /> Kopiuj
                </Button>
              </div>

              {/* Zakladki */}
              <Tabs value={previewTab} onValueChange={setPreviewTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="grid w-full grid-cols-3 h-9">
                  <TabsTrigger value="content" className="text-xs">Tresc</TabsTrigger>
                  <TabsTrigger value="seo" className="text-xs">SEO</TabsTrigger>
                  <TabsTrigger value="media" className="text-xs">Media</TabsTrigger>
                </TabsList>

                {/* TAB: Tresc */}
                <TabsContent value="content" className="flex-1 overflow-auto mt-3">
                  {editMode ? (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs font-medium">Tytul artykulu</Label>
                        <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                          className="mt-1" placeholder="Tytul..." />
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Tresc artykulu (HTML)</Label>
                        <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)}
                          className="mt-1 min-h-[300px] font-mono text-xs" placeholder="Tresc HTML..." />
                      </div>
                    </div>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <h1 className="text-2xl font-bold mb-4">{editTitle}</h1>
                      <SafeHtml html={editContent} />
                    </div>
                  )}
                </TabsContent>

                {/* TAB: SEO */}
                <TabsContent value="seo" className="flex-1 overflow-auto mt-3">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium">Meta Title</Label>
                      <Input value={editMetaTitle} onChange={(e) => setEditMetaTitle(e.target.value)}
                        className="mt-1" placeholder="Meta tytul strony..." />
                      <p className="text-[10px] text-muted-foreground mt-1">{editMetaTitle.length}/60 znakow</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Meta Description</Label>
                      <Textarea value={editMetaDesc} onChange={(e) => setEditMetaDesc(e.target.value)}
                        className="mt-1 min-h-[80px]" placeholder="Opis meta..." />
                      <p className="text-[10px] text-muted-foreground mt-1">{editMetaDesc.length}/160 znakow</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Tagi (oddzielone przecinkami)</Label>
                      <Input value={editTags} onChange={(e) => setEditTags(e.target.value)}
                        className="mt-1" placeholder="tag1, tag2, tag3..." />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Alt zdjecia glownego</Label>
                      <Input value={editImageAlt} onChange={(e) => setEditImageAlt(e.target.value)}
                        className="mt-1" placeholder="Opis alternatywny zdjecia..." />
                    </div>
                  </div>
                </TabsContent>

                {/* TAB: Media */}
                <TabsContent value="media" className="flex-1 overflow-auto mt-3">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium mb-2 block">Zdjecie glowne artykulu</Label>
                      {selectedImage ? (
                        <div className="relative inline-block">
                          <img src={typeof selectedImage === 'string' ? selectedImage : selectedImage?.url} alt="Featured" className="max-w-[300px] rounded-lg border" />
                          <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-6 w-6"
                            onClick={() => setSelectedImage(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
                          <Image className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          <p className="text-xs">Wybierz zdjecie z biblioteki ponizej</p>
                        </div>
                      )}
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Label className="text-xs font-medium">Biblioteka mediow</Label>
                        <Select value={mediaFilterCategory} onValueChange={setMediaFilterCategory}>
                          <SelectTrigger className="h-7 text-xs w-[150px]">
                            <SelectValue placeholder="Kategoria" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all" className="text-xs">Wszystkie</SelectItem>
                            {mediaCategories.map(cat => (
                              <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input 
                          placeholder="Szukaj..." 
                          value={mediaSearch}
                          onChange={(e) => setMediaSearch(e.target.value)}
                          className="h-7 text-xs w-[150px]"
                        />
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 max-h-[250px] overflow-auto">
                        {mediaLibrary
                          .filter(img => mediaFilterCategory === "all" || img.category === mediaFilterCategory)
                          .filter(img => !mediaSearch || 
                            (img.filename || "").toLowerCase().includes(mediaSearch.toLowerCase()) ||
                            (img.alt_text || "").toLowerCase().includes(mediaSearch.toLowerCase()) ||
                            (img.tags || []).some(t => t.toLowerCase().includes(mediaSearch.toLowerCase()))
                          )
                          .map((img) => (
                          <div key={img.id} 
                            className={`cursor-pointer border-2 rounded-lg overflow-hidden transition-all group relative ${
                              (typeof selectedImage === 'string' ? selectedImage : selectedImage?.url) === img.url 
                                ? "border-primary ring-2 ring-primary/30" 
                                : "border-transparent hover:border-muted-foreground/30"
                            }`}
                            onClick={() => setSelectedImage(img.url)}>
                            <img src={img.url} alt={img.alt_text || "Media"} className="w-full h-20 object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-[8px] text-white truncate">{img.category || "Brak"}</p>
                            </div>
                          </div>
                        ))}
                        {mediaLibrary.length === 0 && (
                          <p className="col-span-4 text-xs text-muted-foreground text-center py-4">Brak mediow w bibliotece</p>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">
                        {mediaLibrary.filter(img => mediaFilterCategory === "all" || img.category === mediaFilterCategory).length} zdjec 
                        {mediaFilterCategory !== "all" && ` w kategorii "${mediaFilterCategory}"`}
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Footer z przyciskami */}
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <div className="text-xs text-muted-foreground">
                  {previewPost.word_count || 0} slow | {previewPost.is_supporting ? "Artykul wspierajacy" : "Artykul glowny"}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPreviewDialog(false)}>
                    Zamknij
                  </Button>
                  <Button size="sm" onClick={handleSavePost} disabled={saving}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Zapisz zmiany
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG PRODUKTOW AFILIACYJNYCH ============ */}
      <Dialog open={affiliateDialog} onOpenChange={setAffiliateDialog}>
        <DialogContent className="sm:max-w-none max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading">Wybierz produkt afiliacyjny</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Pozycja */}
            <div className="flex items-center gap-4">
              <Label className="text-xs font-medium">Wstaw produkt:</Label>
              <Select value={affiliatePosition} onValueChange={setAffiliatePosition}>
                <SelectTrigger className="w-[200px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start" className="text-xs">Na poczatku artykulu</SelectItem>
                  <SelectItem value="end" className="text-xs">Na koncu artykulu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Wyszukiwanie */}
            <div className="flex items-center gap-2">
              <Input 
                placeholder="Szukaj produktu..." 
                value={affiliateSearch}
                onChange={(e) => setAffiliateSearch(e.target.value)}
                className="h-8 text-xs"
              />
              <Select value={affiliateCategory} onValueChange={setAffiliateCategory}>
                <SelectTrigger className="w-[150px] h-8 text-xs">
                  <SelectValue placeholder="Kategoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">Wszystkie</SelectItem>
                  {[...new Set(affiliateProducts.map(p => p.category).filter(Boolean))].map(cat => (
                    <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Lista produktow */}
            <ScrollArea className="h-[350px] pr-2">
              <div className="space-y-2">
                {affiliateProducts
                  .filter(p => affiliateCategory === "all" || p.category === affiliateCategory)
                  .filter(p => !affiliateSearch || 
                    (p.name || "").toLowerCase().includes(affiliateSearch.toLowerCase()) ||
                    (p.description || "").toLowerCase().includes(affiliateSearch.toLowerCase())
                  )
                  .map((product) => (
                  <div key={product.id} 
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors group"
                    onClick={() => handleAddAffiliateProduct(product)}>
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-16 h-16 object-contain rounded border bg-white" />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-sm truncate">{product.name}</h4>
                        <Badge variant="secondary" className="text-[9px] shrink-0">{product.category || "Brak"}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{product.description}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] text-muted-foreground">Uzyc: {product.insertions || 0}</span>
                        <span className="text-[10px] text-muted-foreground">Klikn: {product.clicks || 0}</span>
                        {product.commission && (
                          <Badge variant="outline" className="text-[9px] text-green-600">{product.commission}</Badge>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-8 text-xs gap-1 shrink-0">
                      <Plus className="w-3 h-3" /> Dodaj
                    </Button>
                  </div>
                ))}
                {affiliateProducts.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">Brak produktow afiliacyjnych</p>
                    <p className="text-xs">Dodaj produkty w Panelu Admina → Produkty Afiliacyjne</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAffiliateDialog(false)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG ARTYKULOW WSPIERAJACYCH ============ */}
      <Dialog open={supportDialog} onOpenChange={setSupportDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading flex items-center gap-2">
              <FileStack className="w-5 h-5 text-purple-600" />
              Generuj artykuly wspierajace
            </DialogTitle>
          </DialogHeader>
          
          {supportPost && (
            <div className="space-y-4">
              {/* Info o glownym artykule */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">
                  Artykul glowny
                </p>
                <p className="text-sm font-medium">{supportPost.title}</p>
                {supportPost.published_url && (
                  <a 
                    href={supportPost.published_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {supportPost.published_url}
                  </a>
                )}
              </div>

              {/* Ustawienia */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium">Ile artykulow wygenerowac?</Label>
                  <Select value={String(supportCount)} onValueChange={(v) => setSupportCount(Number(v))}>
                    <SelectTrigger className="h-9 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 artykul</SelectItem>
                      <SelectItem value="2">2 artykuly</SelectItem>
                      <SelectItem value="3">3 artykuly</SelectItem>
                      <SelectItem value="5">5 artykulow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    <strong>Jak to dziala?</strong><br/>
                    AI wygeneruje {supportCount} unikalne artykuly powiazane tematycznie z glownym. 
                    Kazdy artykul bedzie zawieral naturalny link do oryginalnego artykulu. 
                    Artykuly pojawia sie w kolejce jako szkice do dalszej edycji.
                  </p>
                </div>
              </div>

              {/* Wygenerowane artykuly */}
              {generatedSupport.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-emerald-600">
                    Wygenerowano {generatedSupport.length} artykulow:
                  </p>
                  {generatedSupport.map((sa, idx) => (
                    <div key={idx} className="p-2 rounded border bg-emerald-50 dark:bg-emerald-900/20">
                      <p className="text-xs font-medium">{sa.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Strona: {sa.site} | {sa.words} slow | Status: {sa.status}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSupportDialog(false)}>
              Zamknij
            </Button>
            <Button 
              onClick={handleGenerateSupport} 
              disabled={generatingSupport}
              className="gap-1.5 bg-purple-600 hover:bg-purple-700"
            >
              {generatingSupport ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generowanie...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generuj {supportCount} artykulow
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
